import { useEffect, useId, useRef } from "react";
import { cn } from "@/lib/cn";
import { CloseIcon } from "@/components/ui/icons";
import { Button } from "@/components/ui/Button";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Footer actions, right-aligned. The primary action goes last. */
  footer?: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg";
}

const SIZES = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-3xl" } as const;

/**
 * Dialog, built on the native `<dialog>` element.
 *
 * Native rather than a hand-rolled overlay because the browser then owns the
 * three things that are tedious and easy to get wrong: the focus trap, the top
 * layer (so nothing can paint over it and no `z-index` arithmetic is needed),
 * and inert-ing the page behind it. The `::backdrop` pseudo-element is styled
 * instead of rendering a div.
 *
 * Escape is handled by the browser via `cancel`, which is forwarded to
 * `onClose` so React state stays the source of truth for whether it is open.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  children,
  size = "md",
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  // Per-instance, not a constant. A screen typically renders more than one of
  // these (a create dialog and an edit dialog, both mounted, one open), and a
  // shared literal id makes every `aria-labelledby` resolve to whichever
  // happens to come first in the document — so a dialog announces the wrong
  // title.
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // `showModal()` throws if already open, and `close()` on a closed dialog
    // fires a spurious `close` event — so both are guarded on actual state.
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      // The browser dispatches `cancel` for Escape and `close` for
      // `dialog.close()`. Only `cancel` is a user dismissal.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      // Click on the backdrop. The event target is the dialog itself only when
      // the click landed outside its content box.
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      aria-labelledby={titleId}
      className={cn(
        "panel rounded-panel text-content m-auto w-[calc(100%-2rem)] p-0 shadow-lg backdrop:bg-black/50",
        SIZES[size],
      )}
    >
      {/* The inner div stops a click on the padding from reading as a backdrop
          click — the handler above compares against the dialog element. */}
      <div>
        <div className="border-border flex items-start justify-between gap-4 border-b px-5 py-3.5">
          <div className="min-w-0">
            <h2 id={titleId} className="text-content text-sm font-semibold wrap-anywhere">
              {title}
            </h2>
            {description ? (
              <p className="text-content-muted mt-1 text-xs leading-relaxed wrap-anywhere">
                {description}
              </p>
            ) : null}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <CloseIcon className="size-4" />
          </Button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="border-border bg-surface-sunken flex items-center justify-end gap-2 border-t px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </dialog>
  );
}
